import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CalendarClock,
  CheckCircle,
  Copy,
  ExternalLink,
  FileSpreadsheet,
  FolderKanban,
  Loader2,
  ListChecks,
  PlayCircle,
  RefreshCw,
  Search,
  ShieldCheck,
  UploadCloud,
  X,
} from 'lucide-react';
import {
  getMaesterConnectUrl,
  getMaesterOverview,
  depositMaesterGithubCredentials,
  queueMaesterFullScan,
  saveMaesterCertificateCredential,
  saveMaesterConnection,
  saveMaesterFrequency,
  validateMaesterConnection,
  type MaesterClientState,
  type MaesterFrequency,
  type MaesterOverview,
} from '../services/maesterService';
import { extractPdfText, slugify } from '../services/vulnerabilityScanDocxGenerator';

interface MaesterMicrosoft365ModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const FREQUENCY_LABELS: Record<MaesterFrequency, string> = {
  manual: 'Manuel seulement',
  weekly: 'Hebdomadaire',
  monthly: 'Mensuelle',
  quarterly: 'Trimestrielle',
};

type MaesterModalTab = 'analyses' | 'newscan' | 'controls' | 'workshop';
type MaesterDecision = 'apply' | 'discuss' | 'defer' | 'accept_risk';
type MaesterProjectTargetMode = 'new' | 'existing';
type MaesterExistingProjectOption = { id: number; title: string; nom_client?: string | null; status?: string | null };

const MAESTER_TABS: Array<{ key: MaesterModalTab; label: string }> = [
  { key: 'analyses', label: 'Analyses' },
  { key: 'newscan', label: 'Nouvelle analyse' },
  { key: 'controls', label: 'Contrôles échoués' },
  { key: 'workshop', label: 'Atelier de priorisation' },
];

const MAESTER_DECISION_LABELS: Record<MaesterDecision, string> = {
  apply: 'Appliquer maintenant',
  discuss: 'Discussion client',
  defer: 'Reporter',
  accept_risk: 'Accepter le risque',
};

const MAESTER_DECISION_STYLES: Record<MaesterDecision, string> = {
  apply: 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100',
  discuss: 'border-purple-200 bg-purple-50 text-purple-800 hover:bg-purple-100',
  defer: 'border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100',
  accept_risk: 'border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100',
};

const maesterTaskSections = {
  apply: 'Contrôles à appliquer',
  discuss: 'Discussions client',
} as const;

function maesterControlKey(task: ExternalMaesterReport['tasks'][number], index: number) {
  return `${task.reference || task.category || 'control'}-${task.name}-${index}`;
}

type ExternalMaesterReport = {
  id: string;
  fileName: string;
  pageCount: number;
  totalCount: number;
  passedCount: number;
  failedCount: number;
  otherCount: number;
  tasks: Array<{
    severity: string;
    category: string;
    name: string;
    remediation: string;
    reference?: string;
    selected: boolean;
  }>;
};

function formatDate(value?: string | null) {
  if (!value) return '—';
  return new Date(value).toLocaleString('fr-CA', { dateStyle: 'medium', timeStyle: 'short' });
}

function number(value?: number | null) {
  return new Intl.NumberFormat('fr-CA').format(value || 0);
}

function statusLabel(status: string) {
  if (status === 'connected') return 'Connecté';
  if (status === 'permission_error') return 'Permissions à vérifier';
  return 'À connecter';
}

function parseCount(text: string, labels: string[]) {
  for (const label of labels) {
    const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = text.match(new RegExp(`${escaped}\\s*[:\\-]?\\s*(\\d+)`, 'i'));
    if (match) return Number(match[1]) || 0;
  }
  return 0;
}

function parseMaesterPdfText(text: string, fileName: string, pageCount: number): ExternalMaesterReport {
  const lines = text.split(/\r?\n/)
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter((line) => line.length > 6);
  const failedLines = Array.from(new Set(lines.filter((line) => (
    /(failed|fail|échou|echec|échec|non conforme|not compliant)/i.test(line)
    && !/^(failed|fail|échoué|échec)\s*:?\s*\d*$/i.test(line)
  )))).slice(0, 40);
  const tasks = failedLines.length ? failedLines.map((line) => ({
    severity: 'Élevée',
    category: classifyMaesterControl(line),
    name: line.replace(/\b(failed|fail|échoué|échec|non conforme|not compliant)\b/ig, '').replace(/\s{2,}/g, ' ').trim().slice(0, 180) || 'Corriger un contrôle Maester échoué',
    remediation: 'Appliquer la recommandation Maester pour ce contrôle, documenter la preuve, puis valider par un nouveau scan.',
    reference: line.match(/\b(MT\.[A-Za-z0-9_.-]+|CIS\s*[0-9.]+|MS\.[A-Za-z0-9_.-]+)\b/i)?.[0],
    selected: true,
  })) : [{
    severity: 'Élevée',
    category: 'Microsoft 365',
    name: 'Analyser et corriger les contrôles Maester échoués du PDF externe',
    remediation: 'Lire le rapport Maester importé, créer les corrections M365 requises, documenter les preuves, puis relancer un scan de validation.',
    selected: true,
  }];
  const failedCount = parseCount(text, ['Failed', 'Failures', 'Échecs', 'Echecs']) || tasks.length;
  return {
    id: `maester-pdf-${slugify(fileName)}`,
    fileName,
    pageCount,
    totalCount: parseCount(text, ['Total', 'Tests']) || Math.max(tasks.length, failedCount),
    passedCount: parseCount(text, ['Passed', 'Réussis', 'Reussis']),
    failedCount,
    otherCount: parseCount(text, ['Other', 'Autres', 'Skipped']),
    tasks,
  };
}

function classifyMaesterControl(line: string) {
  if (/mfa|multi.?factor|authent/i.test(line)) return 'MFA / authentification';
  if (/conditional access|acc[eè]s conditionnel/i.test(line)) return 'Conditional Access';
  if (/role|admin|privileg/i.test(line)) return 'Rôles administratifs';
  if (/exchange|mail|phish|dkim|dmarc|spf/i.test(line)) return 'Exchange / anti-phishing';
  if (/sharepoint|onedrive|sharing|partage/i.test(line)) return 'SharePoint / OneDrive';
  if (/teams/i.test(line)) return 'Teams';
  if (/audit|log|alert/i.test(line)) return 'Audit / logs';
  if (/defender|secure score|security/i.test(line)) return 'Defender / sécurité';
  if (/device|intune|compliance|appareil/i.test(line)) return 'Appareils / conformité';
  return 'Microsoft 365';
}

function githubEnvironmentFromTenantName(value: string) {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

export default function MaesterMicrosoft365Modal({ isOpen, onClose }: MaesterMicrosoft365ModalProps) {
  const [overview, setOverview] = useState<MaesterOverview | null>(null);
  const [selectedClient, setSelectedClient] = useState<MaesterClientState | null>(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [manualTenantId, setManualTenantId] = useState('');
  const [manualTenantName, setManualTenantName] = useState('');
  const [certificateAppClientId, setCertificateAppClientId] = useState('');
  const [certificatePem, setCertificatePem] = useState('');
  const [privateKeyPem, setPrivateKeyPem] = useState('');
  const [certificateExpiresAt, setCertificateExpiresAt] = useState('');
  const [githubAuthMethod, setGithubAuthMethod] = useState<'github_secret' | 'github_certificate'>('github_secret');
  const [githubClientSecret, setGithubClientSecret] = useState('');
  const [githubPfxBase64, setGithubPfxBase64] = useState('');
  const [githubPfxPassword, setGithubPfxPassword] = useState('');
  const [consentUrl, setConsentUrl] = useState<string | null>(null);
  const [showAdvancedTenantFields, setShowAdvancedTenantFields] = useState(false);
  const [externalMaesterReport, setExternalMaesterReport] = useState<ExternalMaesterReport | null>(null);
  const [maesterRemediationDueDate, setMaesterRemediationDueDate] = useState('');
  const [creatingMaesterProject, setCreatingMaesterProject] = useState(false);
  const [createdMaesterProject, setCreatedMaesterProject] = useState<{ id: number; title: string } | null>(null);
  const [activeMaesterTab, setActiveMaesterTab] = useState<MaesterModalTab>('analyses');
  const [maesterControlDecisions, setMaesterControlDecisions] = useState<Record<string, MaesterDecision>>({});
  const [maesterProjectTargetMode, setMaesterProjectTargetMode] = useState<MaesterProjectTargetMode>('new');
  const [existingMaesterProjectId, setExistingMaesterProjectId] = useState('');
  const [maesterExistingProjectOptions, setMaesterExistingProjectOptions] = useState<MaesterExistingProjectOption[]>([]);
  const [loadingMaesterExistingProjects, setLoadingMaesterExistingProjects] = useState(false);

  async function loadOverview() {
    setLoading(true);
    setMessage(null);
    try {
      const data = await getMaesterOverview();
      setOverview(data);
      setSelectedClient((current) => {
        if (current) return data.clients.find((client) => client.clientId === current.clientId) || data.clients[0] || null;
        return data.clients[0] || null;
      });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Erreur chargement Maester');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (isOpen) void loadOverview();
    if (!isOpen) {
      setOverview(null);
      setSelectedClient(null);
      setSearch('');
      setMessage(null);
      setManualTenantId('');
      setManualTenantName('');
      setCertificateAppClientId('');
      setCertificatePem('');
      setPrivateKeyPem('');
      setCertificateExpiresAt('');
      setGithubAuthMethod('github_secret');
      setGithubClientSecret('');
      setGithubPfxBase64('');
      setGithubPfxPassword('');
      setConsentUrl(null);
      setShowAdvancedTenantFields(false);
      setExternalMaesterReport(null);
      setMaesterRemediationDueDate('');
      setCreatedMaesterProject(null);
      setActiveMaesterTab('analyses');
      setMaesterControlDecisions({});
      setMaesterProjectTargetMode('new');
      setExistingMaesterProjectId('');
      setMaesterExistingProjectOptions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    setManualTenantId(selectedClient?.tenantId || '');
    setManualTenantName(selectedClient?.tenantName || '');
    setCertificateAppClientId(selectedClient?.appClientId || '');
    setCertificateExpiresAt(selectedClient?.certificateExpiresAt ? String(selectedClient.certificateExpiresAt).slice(0, 10) : '');
    setGithubAuthMethod(selectedClient?.authMethod === 'github_certificate' ? 'github_certificate' : 'github_secret');
    setGithubClientSecret('');
    setGithubPfxBase64('');
    setGithubPfxPassword('');
    setCertificatePem('');
    setPrivateKeyPem('');
    setConsentUrl(null);
    setExternalMaesterReport(null);
    setCreatedMaesterProject(null);
    setMaesterControlDecisions({});
    setMaesterProjectTargetMode('new');
    setExistingMaesterProjectId('');
  }, [selectedClient?.clientId]);

  useEffect(() => {
    if (!isOpen || maesterProjectTargetMode !== 'existing') return;
    let cancelled = false;
    setLoadingMaesterExistingProjects(true);
    fetch('/api/projet-v2/projects', { credentials: 'include' })
      .then((response) => response.ok ? response.json() : Promise.reject(new Error('Impossible de charger les projets PRIsME.')))
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : data.projects || [];
        setMaesterExistingProjectOptions(list.map((project: any) => ({
          id: Number(project.id),
          title: project.title || project.nom || `Projet #${project.id}`,
          nom_client: project.nom_client || project.clientName || null,
          status: project.status || null,
        })).filter((project: MaesterExistingProjectOption) => Number.isFinite(project.id)));
      })
      .catch((error) => setMessage(error.message || 'Impossible de charger les projets PRIsME.'))
      .finally(() => { if (!cancelled) setLoadingMaesterExistingProjects(false); });
    return () => { cancelled = true; };
  }, [isOpen, maesterProjectTargetMode]);

  const filteredClients = useMemo(() => {
    const query = search.trim().toLowerCase();
    const clients = overview?.clients || [];
    if (!query) return clients;
    return clients.filter((client) =>
      client.clientName.toLowerCase().includes(query) ||
      String(client.tenantName || '').toLowerCase().includes(query) ||
      String(client.tenantId || '').toLowerCase().includes(query)
    );
  }, [overview, search]);

  const computedGithubEnvironmentName = githubEnvironmentFromTenantName(manualTenantName);


  const maesterWorkshopControls = useMemo(() => {
    return (externalMaesterReport?.tasks || []).map((task, index) => {
      const key = maesterControlKey(task, index);
      const decision = maesterControlDecisions[key] || (index < 12 ? 'apply' : index < 20 ? 'discuss' : 'defer');
      const impact = /crit|mfa|legacy|admin|privileg/i.test(`${task.severity} ${task.name} ${task.category}`) ? 'Impact très élevé' : /élev|high|dkim|exchange|defender/i.test(`${task.severity} ${task.name} ${task.category}`) ? 'Impact élevé' : 'Impact moyen';
      const effort = /mfa|dkim|spf|dmarc/i.test(task.name) ? 'Effort faible' : /guest|byod|invité|appareil|sharing|partage/i.test(`${task.name} ${task.category}`) ? 'Discussion requise' : 'Effort moyen';
      const scoreGain = impact === 'Impact très élevé' ? 8 : impact === 'Impact élevé' ? 4 : 2;
      return { ...task, key, index, decision, impact, effort, scoreGain };
    });
  }, [externalMaesterReport, maesterControlDecisions]);

  const maesterPlanBuckets = useMemo(() => ({
    apply: maesterWorkshopControls.filter((control) => control.decision === 'apply'),
    discuss: maesterWorkshopControls.filter((control) => control.decision === 'discuss'),
    defer: maesterWorkshopControls.filter((control) => control.decision === 'defer'),
    accept_risk: maesterWorkshopControls.filter((control) => control.decision === 'accept_risk'),
  }), [maesterWorkshopControls]);

  const selectedMaesterProjectItems = useMemo(() => maesterWorkshopControls
    .filter((control) => control.decision === 'apply' || control.decision === 'discuss')
    .map((control) => ({
      severity: control.severity,
      category: control.category,
      name: control.name,
      remediation: control.decision === 'discuss'
        ? `Discussion client à tenir avant configuration: ${control.remediation}`
        : control.remediation,
      reference: control.reference,
      selected: true,
      decision: control.decision,
      taskSection: control.decision === 'discuss' ? maesterTaskSections.discuss : maesterTaskSections.apply,
      taskSectionLabel: `Section de tâches: ${control.decision === 'discuss' ? maesterTaskSections.discuss : maesterTaskSections.apply}`,
      scoreGain: control.scoreGain,
    })), [maesterWorkshopControls]);

  const selectedMaesterControl = maesterWorkshopControls[0] || null;
  const currentMaesterScore = externalMaesterReport ? Math.max(0, Math.min(100, Math.round(((externalMaesterReport.passedCount || 0) / Math.max(externalMaesterReport.totalCount || 1, 1)) * 100))) : 0;
  const simulatedMaesterScore = externalMaesterReport ? Math.min(100, currentMaesterScore + maesterPlanBuckets.apply.reduce((sum, control) => sum + control.scoreGain, 0)) : 0;

  function updateMaesterControlDecision(key: string, decision: MaesterDecision) {
    setMaesterControlDecisions((current) => ({ ...current, [key]: decision }));
  }

  async function handleFrequencyChange(client: MaesterClientState, frequency: MaesterFrequency) {
    setSaving(client.clientId);
    setMessage(null);
    try {
      await saveMaesterFrequency(client.clientId, frequency);
      setMessage(`Fréquence Maester mise à jour pour ${client.clientName}.`);
      await loadOverview();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Erreur sauvegarde fréquence');
    } finally {
      setSaving(null);
    }
  }

  async function handleGenerateConsentLink(client: MaesterClientState, openNow = false) {
    setSaving(client.clientId);
    setMessage(null);
    try {
      const result = await getMaesterConnectUrl(client.clientId);
      if (!result.configured || !result.consentUrl) {
        setMessage(result.reason || 'L’app Entra ID BZ Maester n’est pas encore configurée globalement dans PRIsME.');
        return;
      }
      setConsentUrl(result.consentUrl);
      if (openNow) window.open(result.consentUrl, '_blank', 'noopener,noreferrer');
      setMessage(`Lien prêt pour ${client.clientName}. L’administrateur Microsoft 365 du client doit simplement approuver l’app Entra ID BZ.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Erreur génération lien de connexion');
    } finally {
      setSaving(null);
    }
  }

  async function handleCopyConsentUrl() {
    if (!consentUrl) return;
    await navigator.clipboard.writeText(consentUrl);
    setMessage('Lien de consentement copié.');
  }

  async function handleValidateConnection() {
    if (!selectedClient) return;
    const tenantId = manualTenantId || selectedClient.tenantId || '';
    setSaving(selectedClient.clientId);
    setMessage(null);
    try {
      const result = await validateMaesterConnection(selectedClient.clientId, tenantId);
      setMessage(result.validation?.message || 'Validation Microsoft Graph terminée.');
      await loadOverview();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Erreur validation Microsoft Graph');
    } finally {
      setSaving(null);
    }
  }

  async function handleManualSave() {
    if (!selectedClient) return;
    setSaving(selectedClient.clientId);
    setMessage(null);
    try {
      await saveMaesterConnection(selectedClient.clientId, manualTenantId, manualTenantName);
      setMessage(`Tenant Microsoft 365 associé à ${selectedClient.clientName}.`);
      await loadOverview();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Erreur sauvegarde tenant');
    } finally {
      setSaving(null);
    }
  }

  async function handleSaveCertificateCredential() {
    if (!selectedClient) return;
    setSaving(selectedClient.clientId);
    setMessage(null);
    try {
      const result = await saveMaesterCertificateCredential(selectedClient.clientId, {
        tenantId: manualTenantId,
        tenantName: manualTenantName,
        appClientId: certificateAppClientId,
        certificatePem,
        privateKeyPem,
        certificateExpiresAt: certificateExpiresAt || undefined,
      });
      setCertificatePem('');
      setPrivateKeyPem('');
      setMessage(result.validation?.message || `Certificat Maester enregistré pour ${selectedClient.clientName}.`);
      await loadOverview();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Erreur sauvegarde certificat');
    } finally {
      setSaving(null);
    }
  }

  async function handleDepositGithubCredentials() {
    if (!selectedClient) return;
    setSaving(selectedClient.clientId);
    setMessage(`Validation Microsoft Graph et dépôt dans GitHub Secrets en cours pour ${selectedClient.clientName}…`);
    try {
      const result = await depositMaesterGithubCredentials(selectedClient.clientId, {
        tenantId: manualTenantId,
        tenantName: manualTenantName,
        appClientId: certificateAppClientId,
        githubEnvironmentName: computedGithubEnvironmentName,
        authMethod: githubAuthMethod,
        clientSecret: githubAuthMethod === 'github_secret' ? githubClientSecret : undefined,
        certificatePfxBase64: githubAuthMethod === 'github_certificate' ? githubPfxBase64 : undefined,
        certificatePassword: githubAuthMethod === 'github_certificate' ? githubPfxPassword : undefined,
        certificateExpiresAt: certificateExpiresAt || undefined,
      });
      setGithubClientSecret('');
      setGithubPfxBase64('');
      setGithubPfxPassword('');
      setMessage(result.message || `Accès déposés dans GitHub Environment ${result.environmentName}.`);
      await loadOverview();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Erreur dépôt GitHub Secrets');
    } finally {
      setSaving(null);
    }
  }

  async function handleQueueScan(client: MaesterClientState) {
    setSaving(client.clientId);
    setMessage(null);
    try {
      const result = await queueMaesterFullScan(client.clientId, client.tenantId);
      setMessage(result.message || `Scan complet Maester placé en file pour ${client.clientName}.`);
      await loadOverview();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Erreur lancement scan');
    } finally {
      setSaving(null);
    }
  }

  async function handleImportMaesterPdf(file: File | undefined) {
    if (!file) return;
    setMessage('Extraction du PDF Maester externe...');
    try {
      const { text, pageCount } = await extractPdfText(file);
      const report = parseMaesterPdfText(text, file.name, pageCount);
      setExternalMaesterReport(report);
      setCreatedMaesterProject(null);
      setActiveMaesterTab('workshop');
      setMaesterControlDecisions({});
      setMessage(`PDF Maester importé : ${report.failedCount} contrôle(s) échoué(s), ${report.tasks.length} tâche(s) proposées.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Impossible de lire le PDF Maester.');
    }
  }

  async function handleCreateMaesterRemediationProject() {
    if (!selectedClient || !externalMaesterReport) return;
    setCreatingMaesterProject(true);
    setMessage('Création du projet de remédiation Microsoft 365...');
    try {
      const response = await fetch('/api/prisme/cyber/remediation-project', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientName: selectedClient.clientName,
          clientId: null,
          reportTitle: externalMaesterReport.fileName.replace(/\.pdf$/i, ''),
          sourceReportId: externalMaesterReport.id,
          sourceModule: 'maester_scan_remediation',
          sourceSummary: `PDF Maester externe: ${externalMaesterReport.failedCount} contrôle(s) échoué(s)`,
          projectTitle: `Remédiation Microsoft 365 — ${selectedClient.clientName}`,
          taskPrefix: 'M365',
          priorityMinimum: 'Échecs Maester',
          dueDate: maesterRemediationDueDate,
          vulnerabilities: selectedMaesterProjectItems,
          targetProjectMode: maesterProjectTargetMode,
          existingProjectId: maesterProjectTargetMode === 'existing' ? existingMaesterProjectId : undefined,
        }),
      });
      const result = await response.json().catch(() => null);
      if (!response.ok) throw new Error(result?.error || 'Impossible de créer le projet Maester.');
      const project = result.project;
      setCreatedMaesterProject({ id: Number(project.id), title: project.title });
      setMessage(result.created
        ? `Projet de remédiation M365 créé : ${project.title}. ${result.taskCount || selectedMaesterProjectItems.length} tâches générées.`
        : `Tâches Maester ajoutées ou liées au projet : ${project.title}.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Impossible de créer le projet Maester.');
    } finally {
      setCreatingMaesterProject(false);
    }
  }

  if (!isOpen) return null;

  const summary = overview?.summary;
  const selectedScan = selectedClient?.latestScan;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            className="my-8 flex max-h-[92vh] w-full max-w-7xl flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/20"
          >
            <div className="border-b border-slate-200 bg-gradient-to-r from-white via-blue-50 to-slate-50 p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="rounded-2xl bg-blue-50 p-3 text-blue-700 ring-1 ring-blue-200">
                    <ShieldCheck className="h-9 w-9" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-slate-900">Analyses Maester Microsoft 365</h2>
                    <p className="mt-1 text-sm text-slate-600">
                      Connecter les environnements Microsoft 365 clients via approbation d’app Entra ID, puis planifier et lancer les scans complets Maester.
                    </p>
                    <p className="mt-2 text-xs text-slate-500">
                      Un seul type de scan est prévu : tous les tests Maester disponibles pour le tenant connecté.
                    </p>
                  </div>
                </div>
                <button onClick={onClose} className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900">
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="mt-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="relative max-w-md flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Rechercher un client ou tenant…"
                    className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-10 pr-4 text-sm text-slate-900 placeholder:text-slate-500 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => void loadOverview()}
                  disabled={loading}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  Rafraîchir
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto bg-slate-50 p-6">
              {message && (
                <div className="mb-5 rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
                  {message}
                </div>
              )}

              <div className="mb-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Clients PRIsME</p>
                  <p className="mt-2 text-3xl font-bold text-slate-900">{number(summary?.clientCount)}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Tenants connectés</p>
                  <p className="mt-2 text-3xl font-bold text-slate-900">{number(summary?.connectedCount)}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Scans planifiés</p>
                  <p className="mt-2 text-3xl font-bold text-slate-900">{number(summary?.scheduledCount)}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Clients avec résultat</p>
                  <p className="mt-2 text-3xl font-bold text-slate-900">{number(summary?.lastScanCount)}</p>
                </div>
              </div>


              <div className="mb-6 flex flex-wrap gap-2">
                {MAESTER_TABS.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveMaesterTab(tab.key)}
                    className={`rounded-xl px-4 py-2 text-sm font-black transition ${activeMaesterTab === tab.key ? 'bg-blue-600 text-white shadow-lg shadow-blue-200' : 'border border-slate-200 bg-white text-slate-700 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-800'}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {activeMaesterTab === 'analyses' && !loading && overview && (
                <div className="space-y-5">
                  <div className="grid gap-4 lg:grid-cols-3">
                    <div className="rounded-2xl border border-slate-200 bg-white p-5 lg:col-span-2">
                      <div className="mb-4 flex items-center justify-between gap-3">
                        <div>
                          <h3 className="text-xl font-bold text-slate-900">Historique des analyses</h3>
                          <p className="text-sm text-slate-500">Clients connectés, derniers résultats Maester et état de préparation du plan.</p>
                        </div>
                        <button type="button" onClick={() => setActiveMaesterTab('newscan')} className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-400">+ Nouvelle analyse</button>
                      </div>
                      <div className="space-y-3">
                        {filteredClients.map((client) => (
                          <button key={client.clientId} type="button" onClick={() => setSelectedClient(client)} className={`w-full rounded-2xl border p-4 text-left transition ${selectedClient?.clientId === client.clientId ? 'border-blue-300 bg-blue-500/15' : 'border-slate-200 bg-slate-50 hover:border-blue-300/50'}`}>
                            <div className="grid gap-3 md:grid-cols-[1fr_auto_auto_auto] md:items-center">
                              <div>
                                <div className="text-lg font-black text-slate-900">{client.clientName}</div>
                                <div className="text-xs text-slate-500">{client.tenantName || client.tenantId || 'Tenant à connecter'} · {formatDate(client.lastScanAt)}</div>
                              </div>
                              <span className={`rounded-full px-3 py-1 text-xs font-bold ${client.connectionStatus === 'connected' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-500/20 text-slate-700'}`}>{statusLabel(client.connectionStatus)}</span>
                              <span className="rounded-full bg-rose-50 px-3 py-1 text-xs font-bold text-rose-700">{number(client.latestScan?.failedCount)} échoués</span>
                              <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-bold text-amber-700">{client.latestScan ? 'À prioriser' : 'Aucun plan'}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="rounded-2xl border border-blue-300/25 bg-blue-50 p-5">
                        <p className="text-xs font-black uppercase tracking-wide text-blue-700">Analyse active</p>
                        <h3 className="mt-1 text-2xl font-black text-slate-900">{selectedClient?.clientName || 'Aucun client'}</h3>
                        <p className="mt-2 text-sm text-blue-700">{selectedClient?.latestScan ? `${number(selectedClient.latestScan.failedCount)} contrôles échoués au dernier scan.` : 'Importe un PDF ou lance un scan pour alimenter l’atelier.'}</p>
                      </div>
                      <div className="rounded-2xl border border-slate-200 bg-white p-5">
                        <h3 className="text-lg font-bold text-slate-900">Plans à finaliser</h3>
                        <p className="mt-2 text-4xl font-black text-slate-900">{externalMaesterReport ? maesterWorkshopControls.length : 0}</p>
                        <p className="text-sm text-slate-500">Contrôles disponibles dans l’atelier courant.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeMaesterTab === 'controls' && !loading && overview && (
                <div className="grid gap-5 xl:grid-cols-[320px_1fr]">
                  <aside className="rounded-2xl border border-slate-200 bg-white p-5">
                    <h3 className="text-lg font-bold text-slate-900">Filtres</h3>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs font-bold">
                      <span className="rounded-full bg-rose-100 px-3 py-1 text-rose-700">Critiques</span>
                      <span className="rounded-full bg-amber-100 px-3 py-1 text-amber-700">Élevées</span>
                      <span className="rounded-full bg-emerald-100 px-3 py-1 text-emerald-800">Effort faible</span>
                      <span className="rounded-full bg-purple-100 px-3 py-1 text-purple-700">Discussion client</span>
                    </div>
                    <p className="mt-4 text-sm text-slate-500">L’inventaire se nourrit du PDF Maester importé dans Nouvelle analyse. Les décisions se prennent dans l’atelier.</p>
                    <button type="button" onClick={() => setActiveMaesterTab('newscan')} className="mt-4 w-full rounded-xl border border-blue-300/30 bg-blue-50 px-4 py-2 text-sm font-bold text-blue-800 hover:bg-blue-500/15">Importer / lancer une analyse</button>
                  </aside>
                  <section className="rounded-2xl border border-slate-200 bg-white p-5">
                    <div className="mb-4 flex items-center justify-between gap-3">
                      <div>
                        <h3 className="text-xl font-bold text-slate-900">Contrôles échoués</h3>
                        <p className="text-sm text-slate-500">Vue inventaire avant décision.</p>
                      </div>
                      <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-700">{number(maesterWorkshopControls.length)} contrôles</span>
                    </div>
                    {maesterWorkshopControls.length ? <div className="space-y-3">
                      {maesterWorkshopControls.map((control) => (
                        <div key={control.key} className="grid gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 md:grid-cols-[1fr_auto] md:items-center">
                          <div>
                            <div className="font-black text-slate-900">{control.name}</div>
                            <div className="mt-1 text-xs text-slate-500">{control.category} · {control.reference || 'Référence à confirmer'}</div>
                            <div className="mt-2 flex flex-wrap gap-2 text-xs font-bold"><span className="rounded-full bg-rose-50 px-2 py-1 text-rose-700">{control.impact}</span><span className="rounded-full bg-emerald-50 px-2 py-1 text-emerald-800">{control.effort}</span></div>
                          </div>
                          <button type="button" onClick={() => setActiveMaesterTab('workshop')} className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-400">Décider</button>
                        </div>
                      ))}
                    </div> : <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-slate-600">Aucun contrôle échoué chargé. Utilise Nouvelle analyse pour importer un PDF Maester externe ou lancer le scan complet.</div>}
                  </section>
                </div>
              )}

              {activeMaesterTab === 'workshop' && !loading && overview && (
                <div className="grid gap-5 xl:grid-cols-[300px_minmax(0,1fr)_360px]">
                  <aside className="rounded-2xl border border-slate-200 bg-white p-5 xl:sticky xl:top-0 xl:self-start">
                    <div className="flex items-center justify-between gap-2"><h3 className="text-lg font-bold text-slate-900">Inbox des contrôles</h3><span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-700">{number(maesterWorkshopControls.length)}</span></div>
                    <p className="mt-2 text-sm text-slate-500">Chaque contrôle peut être changé de décision avant création du projet.</p>
                    <div className="mt-4 max-h-[520px] space-y-2 overflow-y-auto pr-1">
                      {maesterWorkshopControls.map((control) => (
                        <div key={control.key} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                          <div className="font-bold text-slate-900">{control.name}</div>
                          <div className="mt-1 text-xs text-slate-500">{control.impact} · +{control.scoreGain} pts</div>
                          <select value={control.decision} onChange={(event) => updateMaesterControlDecision(control.key, event.target.value as MaesterDecision)} className="mt-2 w-full rounded-lg border border-white/10 bg-white px-2 py-2 text-xs font-bold text-slate-900 outline-none focus:border-blue-300">
                            {Object.entries(MAESTER_DECISION_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                          </select>
                        </div>
                      ))}
                      {!maesterWorkshopControls.length && <div className="rounded-xl border border-dashed border-slate-300 p-4 text-sm text-slate-500">Importe un PDF Maester pour remplir l’inbox de priorisation.</div>}
                    </div>
                  </aside>
                  <section className="space-y-4">
                    <div className="maesterSelectedControlPanel rounded-2xl border border-blue-200 bg-white p-5 shadow-sm">
                      <span className="rounded-full bg-rose-50 px-3 py-1 text-xs font-black text-rose-700">Contrôle sélectionné</span>
                      <h3 className="mt-3 text-2xl font-black text-slate-900">{selectedMaesterControl?.name || 'Aucun contrôle chargé'}</h3>
                      <p className="mt-2 text-sm text-blue-700">{selectedMaesterControl?.remediation || 'Les contrôles échoués seront classés ici après import ou scan.'}</p>
                      <div className="mt-4 grid gap-3 md:grid-cols-4">
                        <div className="rounded-xl border border-slate-200 bg-white p-3 text-center"><b className="block text-slate-900">Impact</b><span className="text-xs text-slate-600">{selectedMaesterControl?.impact || '—'}</span></div>
                        <div className="rounded-xl border border-slate-200 bg-white p-3 text-center"><b className="block text-slate-900">Effort</b><span className="text-xs text-slate-600">{selectedMaesterControl?.effort || '—'}</span></div>
                        <div className="rounded-xl border border-slate-200 bg-white p-3 text-center"><b className="block text-slate-900">Gain</b><span className="text-xs text-slate-600">+{selectedMaesterControl?.scoreGain || 0} pts</span></div>
                        <div className="rounded-xl border border-slate-200 bg-white p-3 text-center"><b className="block text-slate-900">Décision</b><span className="text-xs text-slate-600">{selectedMaesterControl ? MAESTER_DECISION_LABELS[selectedMaesterControl.decision] : '—'}</span></div>
                      </div>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white p-5">
                      <h3 className="text-xl font-bold text-slate-900">Décisions rapides</h3>
                      <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                        {(Object.keys(MAESTER_DECISION_LABELS) as MaesterDecision[]).map((decision) => (
                          <button key={decision} type="button" disabled={!selectedMaesterControl} onClick={() => selectedMaesterControl && updateMaesterControlDecision(selectedMaesterControl.key, decision)} className={`rounded-2xl border px-4 py-4 text-left text-sm font-black disabled:opacity-40 ${MAESTER_DECISION_STYLES[decision]}`}>
                            {MAESTER_DECISION_LABELS[decision]}<br /><span className="text-xs font-normal opacity-80">{decision === 'apply' ? 'Tâche technique' : decision === 'discuss' ? 'Tâche de rencontre' : decision === 'defer' ? 'Sprint futur' : 'Exception documentée'}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </section>
                  <aside className="space-y-4 xl:sticky xl:top-0 xl:self-start">
                    <div className="rounded-2xl border border-slate-200 bg-white p-5 text-slate-900 shadow-sm">
                      <div className="text-sm text-slate-600">Score actuel</div><div className="text-4xl font-black">{currentMaesterScore}%</div>
                      <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200"><span className="block h-full bg-blue-400" style={{ width: `${currentMaesterScore}%` }} /></div>
                      <div className="mt-5 text-sm text-slate-600">Score après plan</div><div className="text-4xl font-black">{simulatedMaesterScore}%</div>
                      <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200"><span className="block h-full bg-emerald-400" style={{ width: `${simulatedMaesterScore}%` }} /></div>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white p-5">
                      <h3 className="text-xl font-bold text-slate-900">Plan retenu</h3>
                      {(['apply','discuss','defer','accept_risk'] as MaesterDecision[]).map((decision) => (
                        <div key={decision} className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                          <div className="flex items-center justify-between gap-2 text-sm font-black text-slate-900"><span>{MAESTER_DECISION_LABELS[decision]}</span><span>{number(maesterPlanBuckets[decision].length)}</span></div>
                          <div className="mt-2 space-y-1">{maesterPlanBuckets[decision].slice(0, 3).map((control) => <div key={control.key} className="truncate rounded-lg bg-slate-50 px-2 py-1 text-xs text-slate-600">{control.name}</div>)}</div>
                        </div>
                      ))}
                    </div>
                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
                      <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-black text-emerald-800">Étape finale</span>
                      <h3 className="mt-3 text-lg font-black text-slate-900">Projet qui sera créé</h3>
                      <p className="mt-1 text-sm text-emerald-700">{number(selectedMaesterProjectItems.length)} tâche(s) ou discussion(s) seront envoyées au projet.</p>
                      <div className="mt-3 grid grid-cols-2 gap-2">
                        <button type="button" onClick={() => setMaesterProjectTargetMode('new')} className={`rounded-xl px-3 py-2 text-xs font-black ${maesterProjectTargetMode === 'new' ? 'bg-emerald-600 text-white' : 'border border-emerald-200 bg-white text-emerald-800'}`}>Créer un nouveau projet</button>
                        <button type="button" onClick={() => setMaesterProjectTargetMode('existing')} className={`rounded-xl px-3 py-2 text-xs font-black ${maesterProjectTargetMode === 'existing' ? 'bg-emerald-600 text-white' : 'border border-emerald-200 bg-white text-emerald-800'}`}>Ajouter à un projet existant</button>
                      </div>
                      {maesterProjectTargetMode === 'existing' ? (
                        <label className="mt-3 block text-xs font-semibold text-emerald-800">Sélectionner le projet
                          <select value={existingMaesterProjectId} onChange={(event) => setExistingMaesterProjectId(event.target.value)} disabled={loadingMaesterExistingProjects} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-emerald-300">
                            <option value="">{loadingMaesterExistingProjects ? 'Chargement des projets…' : 'Choisir un projet existant'}</option>
                            {maesterExistingProjectOptions.map((project) => <option key={project.id} value={project.id}>{project.title}{project.nom_client ? ` — ${project.nom_client}` : ''}</option>)}
                          </select>
                        </label>
                      ) : null}
                      <div className="mt-3 rounded-xl border border-emerald-200 bg-white p-3 text-xs text-emerald-800">
                        <p className="font-black">Sections de tâches créées</p>
                        <p className="mt-1">Section de tâches — Contrôles à appliquer : {number(maesterPlanBuckets.apply.length)}</p>
                        <p>Section de tâches — Discussions client : {number(maesterPlanBuckets.discuss.length)}</p>
                      </div>
                      <label className="mt-3 block text-xs font-semibold text-emerald-800">Échéance cible<input type="date" value={maesterRemediationDueDate} onChange={(event) => setMaesterRemediationDueDate(event.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-emerald-300" /></label>
                      <button type="button" onClick={() => setActiveMaesterTab('workshop')} disabled={creatingMaesterProject || !selectedClient || !externalMaesterReport || selectedMaesterProjectItems.length === 0 || !!createdMaesterProject || (maesterProjectTargetMode === 'existing' && !existingMaesterProjectId.trim())} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-sm font-bold text-white hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-50"><FolderKanban className="h-4 w-4" />{creatingMaesterProject ? 'Création…' : createdMaesterProject ? 'Projet créé' : 'Créer / alimenter le projet'}</button>
                    </div>
                  </aside>
                </div>
              )}

              {activeMaesterTab === 'newscan' && loading && <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-700">Chargement des clients Maester…</div>}

              {activeMaesterTab === 'newscan' && !loading && overview && (
                <div className="grid gap-5 xl:grid-cols-[1fr_420px]">
                  <div className="space-y-3">
                    {filteredClients.map((client) => {
                      const isSelected = selectedClient?.clientId === client.clientId;
                      const connected = client.connectionStatus === 'connected';
                      return (
                        <button
                          key={client.clientId}
                          type="button"
                          onClick={() => setSelectedClient(client)}
                          className={`w-full rounded-2xl border p-4 text-left transition ${isSelected ? 'border-blue-300 bg-blue-500/15' : 'border-white/10 bg-white/[0.05] hover:border-blue-300/50 hover:bg-white/[0.08]'}`}
                        >
                          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                            <div>
                              <div className="flex flex-wrap items-center gap-2">
                                <h3 className="text-lg font-bold text-slate-900">{client.clientName}</h3>
                                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${connected ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-500/20 text-slate-700'}`}>
                                  {statusLabel(client.connectionStatus)}
                                </span>
                              </div>
                              <p className="mt-1 text-xs text-slate-500">{client.tenantName || client.tenantId || 'Aucun tenant Microsoft 365 associé'}</p>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm text-slate-700 lg:min-w-[360px]">
                              <div>
                                <p className="text-xs text-slate-500">Fréquence</p>
                                <p className="font-semibold">{FREQUENCY_LABELS[client.frequency]}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500">Dernier scan</p>
                                <p className="font-semibold">{formatDate(client.lastScanAt)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500">Failed</p>
                                <p className="font-semibold text-rose-700">{number(client.latestScan?.failedCount)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500">Prochain scan</p>
                                <p className="font-semibold">{formatDate(client.nextScanAt)}</p>
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  <aside className="rounded-2xl border border-slate-200 bg-white p-5">
                    {selectedClient ? (
                      <div className="space-y-5">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Client sélectionné</p>
                          <h3 className="mt-1 text-2xl font-bold text-slate-900">{selectedClient.clientName}</h3>
                          <p className="mt-1 text-sm text-slate-500">{statusLabel(selectedClient.connectionStatus)}</p>
                        </div>

                        <div className="space-y-3 rounded-xl border border-emerald-300/20 bg-emerald-50 p-4">
                          <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800">
                            <ShieldCheck className="h-4 w-4" />
                            Déposer les accès dans GitHub Secrets
                          </div>
                          <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800">
                            <p className="font-black">Flux recommandé : dépôt GitHub Secrets write-only</p>
                            <p className="mt-1">PRIsME valide les accès, les dépose dans le GitHub Environment du client, puis ne conserve que les métadonnées utiles.</p>
                          </div>
                          <p className="text-xs text-emerald-700">
                            PRIsME utilise ces valeurs une seule fois pour créer/mettre à jour les secrets GitHub du client. Les valeurs sensibles ne seront pas affichées ni conservées dans PRIsME.
                          </p>
                          {overview?.githubSecrets && !overview.githubSecrets.hasToken && (
                            <div className="rounded-xl border border-amber-300/30 bg-amber-500/10 p-3 text-xs text-amber-700">
                              Dépôt GitHub non configuré côté serveur : la variable MAESTER_GITHUB_TOKEN doit être présente pour créer l’environnement et déposer les secrets.
                            </div>
                          )}
                          {saving === selectedClient.clientId && (
                            <div className="rounded-xl border border-emerald-300/30 bg-emerald-400/10 p-3 text-xs text-emerald-800">
                              Validation Microsoft Graph et dépôt GitHub en cours… Cette étape peut prendre quelques secondes.
                            </div>
                          )}
                          <input
                            value={manualTenantName}
                            onChange={(event) => setManualTenantName(event.target.value)}
                            placeholder="Nom du tenant / client Microsoft 365"
                            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-emerald-300"
                          />
                          <input
                            value={manualTenantId}
                            onChange={(event) => setManualTenantId(event.target.value)}
                            placeholder="Tenant ID Microsoft"
                            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-emerald-300"
                          />
                          <input
                            value={certificateAppClientId}
                            onChange={(event) => setCertificateAppClientId(event.target.value)}
                            placeholder="Application / Client ID"
                            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-emerald-300"
                          />
                          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-emerald-800">
                            <p className="font-semibold">GitHub Environment généré automatiquement</p>
                            <p className="mt-1 font-mono text-emerald-800">{computedGithubEnvironmentName || 'Saisis le nom du tenant/client'}</p>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              type="button"
                              onClick={() => setGithubAuthMethod('github_secret')}
                              className={`rounded-xl px-3 py-2 text-xs font-semibold ${githubAuthMethod === 'github_secret' ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                            >
                              Client secret
                            </button>
                            <button
                              type="button"
                              onClick={() => setGithubAuthMethod('github_certificate')}
                              className={`rounded-xl px-3 py-2 text-xs font-semibold ${githubAuthMethod === 'github_certificate' ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                            >
                              Certificat PFX
                            </button>
                          </div>
                          {githubAuthMethod === 'github_secret' ? (
                            <input
                              value={githubClientSecret}
                              onChange={(event) => setGithubClientSecret(event.target.value)}
                              type="password"
                              autoComplete="off"
                              placeholder="Client secret — dépôt unique dans GitHub"
                              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-emerald-300"
                            />
                          ) : (
                            <div className="space-y-2">
                              <textarea
                                value={githubPfxBase64}
                                onChange={(event) => setGithubPfxBase64(event.target.value)}
                                placeholder="PFX en base64 — dépôt unique dans GitHub"
                                rows={3}
                                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-none placeholder:text-slate-400 focus:border-emerald-300"
                              />
                              <input
                                value={githubPfxPassword}
                                onChange={(event) => setGithubPfxPassword(event.target.value)}
                                type="password"
                                autoComplete="off"
                                placeholder="Mot de passe du PFX"
                                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-emerald-300"
                              />
                              <input
                                value={certificateExpiresAt}
                                onChange={(event) => setCertificateExpiresAt(event.target.value)}
                                type="date"
                                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-emerald-300"
                              />
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => void handleDepositGithubCredentials()}
                            disabled={saving === selectedClient.clientId || !manualTenantId || !manualTenantName.trim() || !computedGithubEnvironmentName || !certificateAppClientId || (githubAuthMethod === 'github_secret' ? !githubClientSecret : (!githubPfxBase64 || !githubPfxPassword))}
                            className="w-full rounded-xl bg-emerald-600 px-4 py-3 text-sm font-bold text-white hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {saving === selectedClient.clientId ? 'Validation et dépôt en cours…' : 'Valider et déposer dans GitHub Secrets'}
                          </button>
                          {selectedClient.githubEnvironmentName && (
                            <div className="rounded-xl border border-emerald-300/20 bg-slate-50 p-3 text-xs text-emerald-800">
                              <p className="font-semibold">Configuré dans GitHub : {selectedClient.githubEnvironmentName}</p>
                              <p className="mt-1 text-emerald-700">Dernier dépôt : {formatDate(selectedClient.githubSecretsConfiguredAt)}</p>
                            </div>
                          )}
                        </div>

                        <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
                          <div className="flex items-center gap-2 text-sm font-semibold text-slate-100">
                            <ExternalLink className="h-4 w-4" />
                            Connexion Entra ID héritée
                          </div>
                          <div className="grid gap-2">
                            <button
                              type="button"
                              onClick={() => void handleGenerateConsentLink(selectedClient, true)}
                              disabled={saving === selectedClient.clientId}
                              className="w-full rounded-xl bg-blue-600 px-4 py-3 text-sm font-bold text-white hover:bg-blue-400 disabled:opacity-50"
                            >
                              Dépôt GitHub recommandé non disponible? Utiliser l’ancien lien d’approbation BZ
                            </button>
                            {consentUrl && (
                              <div className="rounded-xl border border-blue-300/20 bg-blue-50 p-3">
                                <p className="mb-2 text-xs font-semibold text-blue-800">Lien d’approbation administrateur</p>
                                <p className="break-all text-[11px] text-blue-700">{consentUrl}</p>
                                <div className="mt-3 flex gap-2">
                                  <button type="button" onClick={() => void handleCopyConsentUrl()} className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-900 hover:bg-slate-200"><Copy className="h-3 w-3" /> Copier</button>
                                  <button type="button" onClick={() => window.open(consentUrl, '_blank', 'noopener,noreferrer')} className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-900 hover:bg-slate-200"><ExternalLink className="h-3 w-3" /> Ouvrir</button>
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="grid gap-2">
                            <button
                              type="button"
                              onClick={() => void handleValidateConnection()}
                              disabled={saving === selectedClient.clientId || !selectedClient.tenantId}
                              className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-300/30 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-50 disabled:opacity-50"
                            >
                              <CheckCircle className="h-4 w-4" /> Valider la connexion
                            </button>
                            {!selectedClient.tenantId && (
                              <p className="text-xs text-slate-500">Après l’approbation de l’app par l’admin client, PRIsME associe automatiquement le tenant.</p>
                            )}
                            {selectedClient.validationDetails?.checks && selectedClient.validationDetails.checks.length > 0 && (
                              <div className="mt-2 space-y-1 rounded-xl border border-slate-200 bg-slate-50 p-3">
                                {selectedClient.validationDetails.checks.map((check) => (
                                  <div key={check.key} className="flex items-center justify-between gap-2 text-xs">
                                    <span className="text-slate-600">{check.label}</span>
                                    <span className={check.ok ? 'text-emerald-700' : 'text-amber-700'}>{check.ok ? 'OK' : check.message}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            <button
                              type="button"
                              onClick={() => setShowAdvancedTenantFields((value) => !value)}
                              className="text-left text-xs font-semibold text-blue-700 hover:text-blue-800"
                            >
                              {showAdvancedTenantFields ? 'Masquer les options avancées' : 'Options avancées'}
                            </button>
                            {showAdvancedTenantFields && (
                              <div className="grid gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
                                <p className="text-xs text-slate-500">Réservé aux admins PRIsME si un tenant doit être associé manuellement.</p>
                                <input
                                  value={manualTenantName}
                                  onChange={(event) => setManualTenantName(event.target.value)}
                                  placeholder="Nom du tenant"
                                  className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-blue-300"
                                />
                                <input
                                  value={manualTenantId}
                                  onChange={(event) => setManualTenantId(event.target.value)}
                                  placeholder="Tenant ID Microsoft"
                                  className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-blue-300"
                                />
                                <button
                                  type="button"
                                  onClick={() => void handleManualSave()}
                                  disabled={saving === selectedClient.clientId}
                                  className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-100 hover:bg-slate-200 disabled:opacity-50"
                                >
                                  Sauvegarder le tenant manuellement
                                </button>
                                <div className="mt-3 border-t border-white/10 pt-3">
                                  <p className="text-xs font-semibold text-blue-800">App client avec certificat</p>
                                  <p className="mt-1 text-xs text-slate-500">Le client crée l’app dans son tenant. PRIsME conserve le certificat chiffré et valide Graph sans app BZ centrale.</p>
                                  <input
                                    value={certificateAppClientId}
                                    onChange={(event) => setCertificateAppClientId(event.target.value)}
                                    placeholder="Application / Client ID"
                                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-blue-300"
                                  />
                                  <input
                                    value={certificateExpiresAt}
                                    onChange={(event) => setCertificateExpiresAt(event.target.value)}
                                    type="date"
                                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-blue-300"
                                  />
                                  <textarea
                                    value={certificatePem}
                                    onChange={(event) => setCertificatePem(event.target.value)}
                                    placeholder="Certificat public PEM"
                                    rows={4}
                                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-none placeholder:text-slate-400 focus:border-blue-300"
                                  />
                                  <textarea
                                    value={privateKeyPem}
                                    onChange={(event) => setPrivateKeyPem(event.target.value)}
                                    placeholder="Clé privée PEM — chiffrée au repos dans PRIsME"
                                    rows={4}
                                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-none placeholder:text-slate-400 focus:border-blue-300"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => void handleSaveCertificateCredential()}
                                    disabled={saving === selectedClient.clientId || !manualTenantId || !certificateAppClientId || !certificatePem || !privateKeyPem}
                                    className="mt-2 w-full rounded-xl border border-emerald-300/30 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-50 disabled:opacity-50"
                                  >
                                    Enregistrer le certificat et valider Graph
                                  </button>
                                  {selectedClient.hasCertificateCredential && (
                                    <p className="mt-2 text-xs text-emerald-700">Certificat configuré. Empreinte : {selectedClient.certificateThumbprint || '—'}</p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
                          <div className="flex items-center gap-2 text-sm font-semibold text-slate-100">
                            <CalendarClock className="h-4 w-4" />
                            Fréquence du scan complet
                          </div>
                          <select
                            value={selectedClient.frequency}
                            onChange={(event) => void handleFrequencyChange(selectedClient, event.target.value as MaesterFrequency)}
                            className="w-full rounded-xl border border-white/10 bg-white px-3 py-3 text-sm text-slate-900 outline-none focus:border-blue-300"
                          >
                            {Object.entries(FREQUENCY_LABELS).map(([value, label]) => (
                              <option key={value} value={value}>{label}</option>
                            ))}
                          </select>
                          <p className="text-xs text-slate-500">Le scan exécute toujours tous les tests Maester disponibles.</p>
                        </div>

                        <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
                          <div className="flex items-center gap-2 text-sm font-semibold text-slate-100">
                            <PlayCircle className="h-4 w-4" />
                            Scan complet Maester
                          </div>
                          <button
                            type="button"
                            onClick={() => void handleQueueScan(selectedClient)}
                            disabled={saving === selectedClient.clientId || !selectedClient.tenantId}
                            className="w-full rounded-xl bg-emerald-600 px-4 py-3 text-sm font-bold text-white hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Lancer le scan complet
                          </button>
                          {!selectedClient.tenantId && <p className="text-xs text-slate-500">Associe un tenant Microsoft 365 avant de lancer un scan.</p>}
                        </div>

	                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
	                          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-100">
	                            <FileSpreadsheet className="h-4 w-4" />
	                            Dernier résultat
	                          </div>
                          {selectedScan ? (
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div><p className="text-slate-500">Total</p><p className="font-bold text-slate-900">{number(selectedScan.totalCount)}</p></div>
                              <div><p className="text-slate-500">Passed</p><p className="font-bold text-emerald-700">{number(selectedScan.passedCount)}</p></div>
                              <div><p className="text-slate-500">Failed</p><p className="font-bold text-rose-700">{number(selectedScan.failedCount)}</p></div>
                              <div><p className="text-slate-500">Autres</p><p className="font-bold text-slate-700">{number(selectedScan.otherCount)}</p></div>
                              <div className="col-span-2"><p className="text-slate-500">Complété</p><p className="font-semibold text-slate-900">{formatDate(selectedScan.completedAt)}</p></div>
                            </div>
	                          ) : (
	                            <p className="text-sm text-slate-500">Aucun résultat Maester importé pour ce client.</p>
	                          )}
	                        </div>

	                        <div className="rounded-xl border border-blue-300/20 bg-blue-50 p-4">
	                          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-blue-800">
	                            <UploadCloud className="h-4 w-4" />
	                            PDF Maester externe
	                          </div>
	                          <p className="text-xs leading-5 text-blue-700">
	                            Utilise cette entrée quand le scan Maester a été exécuté hors PRIsME. Le PDF est lu localement dans le navigateur et peut ensuite générer un projet non assigné.
	                          </p>
	                          <label className="mt-3 inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-blue-300/30 bg-slate-100 px-4 py-3 text-sm font-bold text-blue-800 transition hover:bg-slate-200">
	                            <input type="file" accept=".pdf,application/pdf" className="hidden" onChange={(event) => void handleImportMaesterPdf(event.target.files?.[0])} />
	                            <UploadCloud className="h-4 w-4" />
	                            Importer un PDF Maester
	                          </label>
	                          {externalMaesterReport ? (
	                            <div className="mt-3 space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
	                              <div className="grid grid-cols-2 gap-3 text-sm">
	                                <div><p className="text-slate-500">Fichier</p><p className="break-words font-semibold text-slate-900">{externalMaesterReport.fileName}</p></div>
	                                <div><p className="text-slate-500">Pages</p><p className="font-bold text-slate-900">{number(externalMaesterReport.pageCount)}</p></div>
	                                <div><p className="text-slate-500">Failed</p><p className="font-bold text-rose-700">{number(externalMaesterReport.failedCount)}</p></div>
	                                <div><p className="text-slate-500">Tâches proposées</p><p className="font-bold text-blue-800">{number(externalMaesterReport.tasks.length)}</p></div>
	                              </div>
	                              <label className="block text-xs font-semibold text-slate-600">
	                                Échéance cible
	                                <input type="date" value={maesterRemediationDueDate} onChange={(event) => setMaesterRemediationDueDate(event.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-300" />
	                              </label>
	                              <div className="max-h-36 space-y-2 overflow-y-auto pr-1">
	                                {externalMaesterReport.tasks.slice(0, 6).map((task, index) => (
	                                  <div key={`${task.name}-${index}`} className="flex items-start gap-2 rounded-lg bg-slate-50 p-2 text-xs text-slate-700">
	                                    <ListChecks className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-700" />
	                                    <span>{task.name}</span>
	                                  </div>
	                                ))}
	                              </div>
	                              <button
	                                type="button"
	                                onClick={() => void handleCreateMaesterRemediationProject()}
	                                disabled={!externalMaesterReport?.tasks.length}
	                                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-sm font-bold text-white hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-50"
	                              >
	                                <FolderKanban className="h-4 w-4" />
	                                Ouvrir l’atelier de priorisation
	                              </button>
	                            </div>
	                          ) : null}
	                        </div>
	                      </div>
                    ) : (
                      <div className="py-10 text-center text-slate-500">Sélectionne un client.</div>
                    )}
                  </aside>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
